import React from 'react';
import { Spinner } from 'react-bootstrap';
import { FaClipboard, FaThermometerHalf, FaTint } from 'react-icons/fa';
import { YButton } from '../../../ui/YButton';
import { YSelect } from '../../../ui/YSelect';

function SettingsPanel({ icon: Icon, title, children }) {
  return (
    <section className="plant-asst-settings-panel">
      <div className="plant-asst-settings-panel__header">
        {Icon ? <Icon aria-hidden="true" /> : null}
        <span>{title}</span>
      </div>
      <div className="plant-asst-settings-panel__body">{children}</div>
    </section>
  );
}

function Field({ id, label, hint, children }) {
  return (
    <div className="plant-asst-form-field">
      <label className="plant-asst-form-field__label" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint ? <p className="plant-asst-form-field__hint">{hint}</p> : null}
    </div>
  );
}

export function PlantingGroupSettingsForm({
  idPrefix = 'group',
  intro,
  groupName,
  onGroupNameChange,
  groupNote,
  onGroupNoteChange,
  variant = 'create',
  zoneLabel,
  manualOverride,
  onManualOverrideChange,
  zoneCode,
  onZoneCodeChange,
  zoneOptionsList = [],
  formatZoneOptionLabel,
  onDetectZone,
  detectZoneLoading = false,
  detectZoneError,
  showDetectZone = false,
  soilType,
  onSoilTypeChange,
  soilTypeOptions = [],
  soilDrainage,
  onSoilDrainageChange,
  soilDrainageOptions = [],
  soilPh,
  onSoilPhChange,
  phHelperText,
  soilPhGuidanceBlurb,
  soilNotes,
  onSoilNotesChange,
  soilNotePlaceholder = '',
  optionsLoading = false,
  optionsError,
  footer,
  errorMessage,
  readOnly = false,
}) {
  const nameId = `${idPrefix}GroupName`;
  const noteId = `${idPrefix}GroupNote`;
  const zoneSwitchId = `${idPrefix}ManualZoneSwitch`;
  const zoneSelectId = `${idPrefix}ZoneSelect`;
  const soilTypeId = `${idPrefix}SoilType`;
  const soilDrainageId = `${idPrefix}SoilDrainage`;
  const soilPhId = `${idPrefix}SoilPh`;
  const soilNotesId = `${idPrefix}SoilNotes`;

  return (
    <div className="plant-asst-group-settings" data-testid="plant-asst-group-settings">
      {intro ? <p className="plant-asst-group-settings__intro">{intro}</p> : null}

      <SettingsPanel icon={FaClipboard} title="Group details">
        <Field id={nameId} label="Group name">
          <input
            id={nameId}
            type="text"
            className="y-input plant-asst-form-field__control"
            value={groupName}
            onChange={(e) => onGroupNameChange(e.target.value)}
            placeholder="Food plots 2025"
            readOnly={readOnly}
            disabled={readOnly}
          />
        </Field>
        <Field id={noteId} label="Notes">
          <textarea
            id={noteId}
            className="y-input plant-asst-form-field__control plant-asst-form-field__textarea"
            rows={2}
            value={groupNote}
            onChange={(e) => onGroupNoteChange(e.target.value)}
            placeholder="Optional context for this group"
            readOnly={readOnly}
            disabled={readOnly}
          />
        </Field>
      </SettingsPanel>

      <SettingsPanel icon={FaThermometerHalf} title="USDA hardiness zone">
        {variant === 'edit' && zoneLabel ? (
          <p className="plant-asst-zone-status">
            Current zone: <strong>{zoneLabel}</strong>
          </p>
        ) : (
          <p className="plant-asst-zone-status plant-asst-zone-status--muted">
            Set the zone manually below, or save the group and use detect from map when editing.
          </p>
        )}
        {showDetectZone && (
          <div className="plant-asst-zone-actions">
            <YButton
              variant="secondary"
              size="sm"
              disabled={detectZoneLoading}
              onClick={onDetectZone}
            >
              {detectZoneLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Detecting zone…
                </>
              ) : (
                'Detect zone from map area'
              )}
            </YButton>
            {detectZoneError ? (
              <p className="plant-asst-form-field__error" role="alert">
                {detectZoneError}
              </p>
            ) : null}
          </div>
        )}
        <div className="plant-asst-form-switch">
          <input
            type="checkbox"
            id={zoneSwitchId}
            className="plant-asst-form-switch__input"
            checked={manualOverride}
            disabled={readOnly || optionsLoading || zoneOptionsList.length === 0}
            onChange={(e) => onManualOverrideChange(e.target.checked)}
          />
          <label className="plant-asst-form-switch__label" htmlFor={zoneSwitchId}>
            Manual zone override
          </label>
        </div>
        {manualOverride && (
          <Field id={zoneSelectId} label="Select zone">
            <YSelect
              id={zoneSelectId}
              className="plant-asst-form-field__control"
              aria-label="Select a USDA zone"
              value={zoneCode}
              disabled={readOnly || optionsLoading || zoneOptionsList.length === 0}
              onChange={(e) => onZoneCodeChange(e.target.value)}
            >
              <option value="">Select zone</option>
              {zoneOptionsList.map((zone) => (
                <option key={zone.value} value={zone.value}>
                  {formatZoneOptionLabel(zone)}
                </option>
              ))}
            </YSelect>
          </Field>
        )}
      </SettingsPanel>

      <SettingsPanel icon={FaTint} title="Soil profile">
        <div className="plant-asst-form-grid">
          <Field id={soilTypeId} label="Soil type">
            <YSelect
              id={soilTypeId}
              className="plant-asst-form-field__control"
              aria-label="Soil type"
              value={soilType}
              disabled={readOnly || (optionsLoading && soilTypeOptions.length === 0)}
              onChange={(e) => onSoilTypeChange(e.target.value)}
            >
              <option value="">Not sure / mixed</option>
              {soilTypeOptions.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </YSelect>
          </Field>
          <Field id={soilDrainageId} label="Soil drainage">
            <YSelect
              id={soilDrainageId}
              className="plant-asst-form-field__control"
              aria-label="Soil drainage"
              value={soilDrainage}
              disabled={readOnly || (optionsLoading && soilDrainageOptions.length === 0)}
              onChange={(e) => onSoilDrainageChange(e.target.value)}
            >
              <option value="">Select drainage</option>
              {soilDrainageOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </YSelect>
          </Field>
        </div>
        <Field
          id={soilPhId}
          label="Soil pH (optional)"
          hint={phHelperText || soilPhGuidanceBlurb || null}
        >
          <input
            id={soilPhId}
            type="number"
            inputMode="decimal"
            step="0.1"
            min="0"
            max="14"
            className="y-input plant-asst-form-field__control"
            value={soilPh}
            onChange={(e) => onSoilPhChange(e.target.value)}
            placeholder="6.5"
            readOnly={readOnly}
            disabled={readOnly}
          />
        </Field>
        <Field id={soilNotesId} label="Soil notes">
          <textarea
            id={soilNotesId}
            className="y-input plant-asst-form-field__control plant-asst-form-field__textarea"
            rows={2}
            value={soilNotes}
            onChange={(e) => onSoilNotesChange(e.target.value)}
            placeholder={soilNotePlaceholder}
            readOnly={readOnly}
            disabled={readOnly}
          />
        </Field>
      </SettingsPanel>

      {optionsError ? (
        <p className="plant-asst-form-field__warning" role="status">
          {optionsError}
        </p>
      ) : null}
      {errorMessage ? (
        <p className="plant-asst-form-field__error" role="alert">
          {errorMessage}
        </p>
      ) : null}
      {footer ? <div className="plant-asst-group-settings__footer">{footer}</div> : null}
    </div>
  );
}
